import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import useUIStore from '../../store/ui';
import { useSearchOverlay } from '@fabric-app/business/search/hooks/useSearchOverlay';
import { useAnalytics } from '@fabric-app/business/analytics/hooks/useAnalytics';
import { AnalyticsEvents } from '@fabric-app/business/analytics/analytics.types';
import { useQueryResourceList } from '@fabric-app/business/resources/queries/useQueryResourceList';
import { DashboardPageWrapper } from '@fabric-app/business/dashboard/components/DashboardPageWrapper';
import { FolderSkeletonHeader } from '../../modules/folder/views/FolderView/SkeletonHeader';
import { IntegrationFolderHeader } from '../../modules/folder/views/FolderView/IntegrationFolderHeader';
import { FolderHeader } from '../../modules/folder/views/FolderView/FolderHeader';
import { DashboardContent } from '@fabric-app/business/dashboard/components/DashboardContent';
import { useQueryInbox } from '@fabric-app/business/connections/queries/useQueryInbox';
import { pick } from '@fabric-app/utils/object/manipulation';
import { useQueryResourceRoot } from '@fabric-app/business/resource-roots/queries/useQueryResourceRoot';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { useQueryFolder } from '@fabric-app/business/resources/queries/useQueryFolder';
import { isResourceDetailFolder } from '@fabric-app/business/resources/utils/resourceDetailGuards';
import dynamic from 'next/dynamic';
import {
  spaceViewModes,
  type ResourceDetail,
  type ResourceDetailFolder,
} from '@fabric-app/types/resources';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { generatePath } from '@fabric-app/utils/url/routing';
import { useSocketIOParentRoomEventListener } from '@fabric-app/business/socket.io/ParentRoom/useSocketIOParentRoomEventListener';
import { useResourcePermissionsByCurrentUser } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { ResourceListContextPreferencesProvider } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { Image } from '@fabric-app/ui/components/Image';
import { BottomFlair } from '@/src/components/BottomFlair/BottomFlair';
import { withSocketIOParentRoomProvider } from '@fabric-app/business/socket.io/ParentRoom/SocketIOParentRoomProvider';
import { withSpacerRoomProvider } from '@fabric-app/business/multiplayer/config/spaces.config';
import { ResourceFolderEditorialHeader } from '@fabric-app/business/resources/components/ResourceFolderEditorialHeader';
import { useGetFolderIntegration } from '@fabric-app/business/folder/hooks/useGetFolderIntegration';
import { useListViewPreferences } from '@fabric-app/business/resources/hooks/useListViewPreferences';
import { RootType } from '@fabric/woody-client';
import { useResourceSwitcher } from '@fabric-app/business/resource-detail/contexts/resourcesSwitcher';
import { PublishedFlair } from '@fabric-app/business/permission/components/PublishedFlair/PublishedFlair';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';

const FdocList = dynamic(() => import('../../components/FdocList/FdocList'), { ssr: false });

const integrationReadOnlyName = (integration: string) => {
  switch (integration) {
    case 'GOOGLE_DRIVE':
      return 'Google Drive';
    case 'IOS_DEVICE':
      return 'your original iOS Screenshots';
    case 'ANDROID_DEVICE':
      return 'your original Android Screenshots';
    case 'GITHUB':
      return 'GitHub';
    default:
      return 'the connection';
  }
};

/**
 * !!! partially refactored
 * This view has been refactored - splitted into screenshots root, decoupled headers if the folder is integration or not
 * it could probably be decoupled further
 * -- integration folders don't need deleting, creating etc
 * -- there's some specific polling for ios/gdrive connections
 */
const ExpandedFolder: React.FC<{
  resourceId: string;
  readOnly?: boolean;
  overrideOnClose?: VoidFunction | null;
  onClickFolder?: (resourceFolder: ResourceDetailFolder | string) => void;
  onClickResource?: (resource: ResourceDetail) => void;
}> = ({
  resourceId: folderId,
  readOnly = false,
  overrideOnClose,
  onClickFolder,
  onClickResource,
}) => {
  const router = useRouter();
  const { inboxRoot } = useQueryInbox();
  const { isMobileView } = useResponsive();

  const { searchQuery, filter, zoomLevel, setZoomLevel } = useUIStore(
    (s) => pick(s, ['searchQuery', 'filter', 'zoomLevel', 'setZoomLevel']),
    shallow,
  );

  /************************************************************************************************************************
   * folder data
   */
  const { folder, isLoading, error } = useQueryFolder({ resourceId: folderId });

  const { savePreferences, preferences } = useListViewPreferences(folder);
  const shareToken = useContextShareToken();

  /**
   * redirect to /space if folder is root of space
   */
  useRunOnce(
    () => {
      router.replace(generatePath(appRoutes.space.detail, { spaceId: folder!.id }));
    },
    {
      condition:
        !!folder &&
        folder.id === folder?.root?.id &&
        folder.root.type !== RootType.INTEGRATION &&
        !shareToken,
    },
  );

  /**
   * redirect to 404 if folder is not found
   */
  useRunOnce(
    () => {
      router.replace('/404');
    },
    {
      condition: !!error,
    },
  );

  const folderIntegration = useGetFolderIntegration(folder);

  const { folderIntegration: integration, isFolderRootOfIntegration: isIntegrationRootFolder } =
    folderIntegration;

  const { track } = useAnalytics();
  useRunOnce(() => {
    track(
      folderIntegration.folderIntegration
        ? AnalyticsEvents.OpenIntegration
        : AnalyticsEvents.OpenFolder,
    );
  });

  /**
   * if view mode is sort and the context doesn't support it, override it with grid view
   */
  const disabledKanbanViewMode = folder?.root?.type !== 'SPACE';

  /************************************************************************************************************************
   * Folder resource data
   */

  const { asLegacySpace: list } = useQueryResourceRoot(folder?.parent?.id);

  const { isOverlayOpen: isSearchOverlayOpen } = useSearchOverlay();

  const listQueryParams = {
    ...preferences?.filters,
    parentId: folderId,
  };
  const {
    resources,
    total: totalResources,
    isLoading: isLoadingResources,
    refetch,
    fetchNextPage: _fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useQueryResourceList(listQueryParams, {
    refetchInterval: false,
    enabled: !isSearchOverlayOpen,
  });

  const fetchNextPage = !isFetchingNextPage && hasNextPage ? _fetchNextPage : undefined;

  useSocketIOParentRoomEventListener('RESOURCES:RELOAD', () => {
    refetch();
  });

  /************************************************************************************************************************
   * switcher stuff
   */

  const { setResourceIds: setSwitcherResourceIds } = useResourceSwitcher();

  useEffect(() => {
    if (isSearchOverlayOpen) return;
    setSwitcherResourceIds(
      resources.filter((f) => !isResourceDetailFolder(f)).map((n) => n.id),
      {
        contextId: `SPACE-${folderId}`,
        fetchNextPage,
      },
    );
  }, [resources, setSwitcherResourceIds, isSearchOverlayOpen, folderId, fetchNextPage]);

  /************************************************************************************************************************/

  const backRoute = useMemo(() => {
    if (!folder?.parent?.id || isLoading) return appRoutes.home;

    if (isIntegrationRootFolder) return appRoutes.home;
    else if (integration) return `/folders/${folder.parent.id}`;

    if (inboxRoot?.id === folder.parent.id) {
      return '/saved';
    }

    if (folder.parent.id === folder?.root?.folder.id) {
      return `/spaces/${folder.parent.id}`;
    }

    return {
      pathname: router.pathname,
      query: { ...router.query, folderId: folder.parent.id },
    };
  }, [
    folder,
    isLoading,
    router.pathname,
    router.query,
    inboxRoot?.id,
    integration,
    isIntegrationRootFolder,
  ]);

  const emptyStateTitle = useMemo(() => {
    if (integration) return 'Connected';

    if (filter !== 'All')
      return `No ${filter.substring(0, filter.length - 1).toLowerCase()} matches`;
    if (searchQuery.length > 0) return 'No matches';
    return undefined;
  }, [filter, searchQuery.length, integration]);

  const emptyStateReason = useMemo(() => {
    if (searchQuery.length > 0) return `No results for "${searchQuery}"`;
    return undefined;
  }, [searchQuery]);

  /**
   * some ui
   */

  const { disabledActions } = useResourcePermissionsByCurrentUser(folder);

  return (
    <ResourceListContextPreferencesProvider
      preferences={preferences}
      savePreferences={savePreferences}
      availableViewModes={disabledKanbanViewMode ? undefined : spaceViewModes}
    >
      {({ viewMode }) => (
        <DashboardPageWrapper>
          {!folder ? (
            <FolderSkeletonHeader overrideOnClose={overrideOnClose} />
          ) : integration ? (
            <IntegrationFolderHeader
              overrideOnClose={overrideOnClose}
              folderIntegration={folderIntegration}
              folder={folder}
              readOnly={readOnly}
            />
          ) : (
            <FolderHeader
              overrideOnClose={overrideOnClose}
              folder={folder}
              backRoute={backRoute}
              readOnly={readOnly}
              resources={resources}
              onClickFolder={onClickFolder}
            />
          )}

          <DashboardContent
            innerProps={{
              disablePadding: true,
              gap: 'none',
              ...(viewMode === 'Kanban' && {
                style: {
                  height: '100%',
                  overflow: 'hidden',
                },
              }),
            }}
          >
            {folder && <PublishedFlair resource={folder} type="folder" />}
            {folder && viewMode !== 'Kanban' && (
              <ResourceFolderEditorialHeader
                viewMode={viewMode}
                disableTitleEdit={!!integration && folder.isReadonly}
                folder={folder}
              />
            )}
            {folder && (
              <FdocList
                layoutId={`folder-${folder.id}`}
                canMultiSelect={!isSearchOverlayOpen && viewMode !== 'Kanban'}
                viewMode={viewMode}
                resources={resources}
                resourcesTotalCount={totalResources}
                isLoading={isLoadingResources}
                onClickFolder={onClickFolder}
                onClickResource={onClickResource}
                onInfiniteTrigger={fetchNextPage}
                queryParams={listQueryParams}
                emptyListMessage={
                  integration
                    ? 'Items from this connection will appear here as they are synchronised'
                    : 'There’s nothing here... Let’s add something!'
                }
                zoomLevel={zoomLevel}
                canModifyContent={!disabledActions.modifyChildResources}
                setZoomLevel={setZoomLevel}
                emptyStateReason={emptyStateReason}
                emptyStateTitle={emptyStateTitle}
                emptyStateImage={
                  integration ? (
                    <Image
                      src={'/images/empty-connection.png'}
                      height={141}
                      width={201}
                      alt="Empty"
                      draggable={false}
                    />
                  ) : undefined
                }
                colorLabels={!integration && list ? list?.labels : undefined}
                spaceId={!integration ? folder?.parent?.id : undefined}
                parent={{
                  id: folder.id,
                  name: folder.name || 'Untitled folder',
                }}
              />
            )}
          </DashboardContent>
          {!isMobileView && folder?.isReadonly && !!integration && (
            <BottomFlair>
              This view is read-only, changes won’t affect the original data in{' '}
              {integrationReadOnlyName(integration.type)}
            </BottomFlair>
          )}
        </DashboardPageWrapper>
      )}
    </ResourceListContextPreferencesProvider>
  );
};

export default withSocketIOParentRoomProvider(withSpacerRoomProvider(ExpandedFolder, 'folder'));
